/* Blazing Banner — print-ready file generator.
   At checkout, each banner is re-rendered from its ORIGINAL source (full-res image,
   vector PDF page, or designer layout) exactly as the customer framed it
   (Show Full Design = contain on white, Fill Entire Banner = cover/crop), then
   wrapped in a PDF whose page is the finished banner size in inches.
   A small JPEG proof is made alongside it. Both are uploaded and their links
   go onto the order line. Exposed as window.BBPrintFile.prepare(set, opts). */
(function () {
  'use strict';

  var MAX_DPI = 150;
  var MIN_DPI = 20; // only reached by extreme sizes (e.g. 50 ft); the device canvas limit wins
  var MAX_UPLOAD_BYTES = 18 * 1024 * 1024; // Shopify file limit is 20 MB
  var PROOF_MAX_SIDE = 1600;

  function isSmallDevice() {
    return window.matchMedia('(max-width: 1179px)').matches ||
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
  }

  // Phones (iOS especially) cap canvas area around 16.7 MP.
  function pixelBudget() {
    return isSmallDevice() ? { area: 16e6, side: 8192 } : { area: 30e6, side: 16000 };
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Could not load artwork image')); };
      img.src = src;
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (b) {
        if (b) resolve(b); else reject(new Error('Could not encode print file (canvas too large?)'));
      }, type, quality);
    });
  }

  function isPdfFile(file) {
    return !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));
  }

  /* ---------- Minimal single-page PDF wrapping one JPEG ---------- */
  function buildPdf(jpegBytes, imgW, imgH, widthIn, heightIn, title) {
    // PDF pages top out at 14400 pt (200 in); bigger banners use /UserUnit.
    var wPt = widthIn * 72, hPt = heightIn * 72;
    var userUnit = 1;
    var maxPt = Math.max(wPt, hPt);
    if (maxPt > 14400) {
      userUnit = Math.ceil(maxPt / 14400);
      wPt /= userUnit; hPt /= userUnit;
    }
    var W = wPt.toFixed(3), H = hPt.toFixed(3);
    var safeTitle = String(title || 'Banner').replace(/[^A-Za-z0-9 ._x-]/g, '');
    var content = 'q ' + W + ' 0 0 ' + H + ' 0 0 cm /Im0 Do Q';

    var enc = new TextEncoder();
    var parts = [];
    var offsets = [];
    var length = 0;
    function push(bytes) { parts.push(bytes); length += bytes.length; }
    function pushText(s) { push(enc.encode(s)); }

    push(new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2D, 0x31, 0x2E, 0x36, 0x0A, 0x25, 0xE2, 0xE3, 0xCF, 0xD3, 0x0A])); // %PDF-1.6 + binary marker

    function obj(n, body) { offsets[n] = length; pushText(n + ' 0 obj\n' + body + '\nendobj\n'); }

    obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
    obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    obj(3, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + W + ' ' + H + '] /TrimBox [0 0 ' + W + ' ' + H + ']' +
      (userUnit > 1 ? ' /UserUnit ' + userUnit : '') +
      ' /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>');
    obj(4, '<< /Length ' + content.length + ' >>\nstream\n' + content + '\nendstream');

    offsets[5] = length;
    pushText('5 0 obj\n<< /Type /XObject /Subtype /Image /Width ' + imgW + ' /Height ' + imgH +
      ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ' + jpegBytes.length + ' >>\nstream\n');
    push(jpegBytes);
    pushText('\nendstream\nendobj\n');

    obj(6, '<< /Title (' + safeTitle + ') /Producer (Blazing Banner Builder) >>');

    var xrefAt = length;
    var xref = 'xref\n0 7\n0000000000 65535 f \n';
    for (var i = 1; i <= 6; i++) xref += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
    pushText(xref + 'trailer\n<< /Size 7 /Root 1 0 R /Info 6 0 R >>\nstartxref\n' + xrefAt + '\n%%EOF\n');

    return new Blob(parts, { type: 'application/pdf' });
  }

  async function upload(api, blob, name) {
    var fd = new FormData();
    fd.append('file', new File([blob], name, { type: blob.type }));
    var res = await fetch(api, { method: 'POST', body: fd });
    var json = await res.json().catch(function () { return {}; });
    if (!res.ok || !json.success || !json.file || !json.file.url) {
      throw new Error(json.error || ('Upload failed for ' + name));
    }
    return json.file.url;
  }

  /* Open the source and report its true dimensions. draw(w, h) returns something
     drawable at roughly w x h pixels (vector sources are rendered at that size). */
  async function openSource(set, kind, opts) {
    if (kind === 'design') {
      if (!window.BBDesigner || !window.BBDesigner.render) throw new Error('Designer not loaded');
      return {
        w: set.design.widthIn, h: set.design.heightIn, vector: true,
        draw: function (w, h) { return window.BBDesigner.render(set.design, w, h); }
      };
    }

    if (kind === 'pdf') {
      var pdfjsLib = await opts.loadPdfJs();
      var pdf = await pdfjsLib.getDocument({ data: await set.sourceFile.arrayBuffer() }).promise;
      var page = await pdf.getPage(1);
      var base = page.getViewport({ scale: 1 });
      return {
        w: base.width, h: base.height, vector: true,
        draw: async function (w) {
          var viewport = page.getViewport({ scale: w / base.width });
          var c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(viewport.width));
          c.height = Math.max(1, Math.round(viewport.height));
          var ctx = c.getContext('2d', { alpha: false });
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, c.width, c.height);
          await page.render({ canvasContext: ctx, viewport: viewport, background: 'rgb(255,255,255)' }).promise;
          return c;
        }
      };
    }

    // Raster: the stored dataUrl is the full-resolution original.
    var src = set.dataUrl || (set.sourceFile ? URL.createObjectURL(set.sourceFile) : '');
    if (!src) throw new Error('No artwork source for this banner');
    var img = await loadImage(src);
    return { w: img.naturalWidth, h: img.naturalHeight, vector: false, draw: function () { return img; } };
  }

  async function prepare(set, opts) {
    if (!set) throw new Error('Missing banner');
    var widthIn = Number(opts.widthIn), heightIn = Number(opts.heightIn);
    if (!(widthIn > 0 && heightIn > 0)) throw new Error('Missing banner size');

    var kind = set.design ? 'design' : (isPdfFile(set.sourceFile) ? 'pdf' : 'raster');

    var src = await openSource(set, kind, opts);
    var srcW = src.w, srcH = src.h;
    if (!(srcW > 0 && srcH > 0)) throw new Error('Artwork dimensions unknown');

    var mode = set.placementMode === 'fit' ? 'fit' : set.placementMode === 'stretch' ? 'stretch' : 'center';
    var fitScale = mode === 'fit'
      ? Math.max(widthIn / srcW, heightIn / srcH)
      : Math.min(widthIn / srcW, heightIn / srcH);
    var boxWIn = mode === 'stretch' ? widthIn : srcW * fitScale; // artwork width on the banner, inches

    // Resolution: as high as the device can handle, capped at 150 DPI, and
    // (for photos) never far past the detail actually in the file.
    var budget = pixelBudget();
    var dpi = Math.min(MAX_DPI,
      Math.sqrt(budget.area / (widthIn * heightIn)),
      budget.side / Math.max(widthIn, heightIn));
    if (!src.vector) dpi = Math.min(dpi, Math.max(72, srcW / boxWIn));
    dpi = Math.max(MIN_DPI, Math.floor(dpi));

    var W = Math.round(widthIn * dpi);
    var H = Math.round(heightIn * dpi);
    // Stretch to fit: the artwork covers the banner exactly (the customer chose to change its shape).
    var dw = mode === 'stretch' ? W : srcW * fitScale * dpi;
    var dh = mode === 'stretch' ? H : srcH * fitScale * dpi;
    var dx = (W - dw) / 2;
    var dy = (H - dh) / 2;

    // Vector sources render at the placed size (capped so "fill" crops can't blow the budget).
    var cap = Math.min(1, Math.sqrt((budget.area * 1.3) / (dw * dh)), budget.side / Math.max(dw, dh));
    var source = await src.draw(Math.max(1, Math.round(dw * cap)), Math.max(1, Math.round(dh * cap)));

    var canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext('2d', { alpha: false });
    // "Match background" fills the sides around a Show Full Design layout with the artwork's edge color.
    ctx.fillStyle = (mode === 'center' && /^#[0-9a-f]{6}$/i.test(set.bgColor || '')) ? set.bgColor : '#ffffff';
    ctx.fillRect(0, 0, W, H);
    if (mode === 'center' && set.bgExtend) {
      // "Extend background": carry the artwork's outermost pixels out to the banner edges
      // (same rule the builder preview uses, so it prints exactly as shown).
      var sw = source.naturalWidth || source.width, sh = source.naturalHeight || source.height;
      var R = Math.max(0, W - dx - dw), B = Math.max(0, H - dy - dh);
      ctx.imageSmoothingEnabled = false;
      if (dx > 0) ctx.drawImage(source, 0, 0, 1, sh, 0, dy, dx + 1, dh);
      if (R > 0) ctx.drawImage(source, sw - 1, 0, 1, sh, dx + dw - 1, dy, R + 1, dh);
      if (dy > 0) ctx.drawImage(source, 0, 0, sw, 1, dx, 0, dw, dy + 1);
      if (B > 0) ctx.drawImage(source, 0, sh - 1, sw, 1, dx, dy + dh - 1, dw, B + 1);
      if (dx > 0 && dy > 0) ctx.drawImage(source, 0, 0, 1, 1, 0, 0, dx + 1, dy + 1);
      if (R > 0 && dy > 0) ctx.drawImage(source, sw - 1, 0, 1, 1, dx + dw - 1, 0, R + 1, dy + 1);
      if (dx > 0 && B > 0) ctx.drawImage(source, 0, sh - 1, 1, 1, 0, dy + dh - 1, dx + 1, B + 1);
      if (R > 0 && B > 0) ctx.drawImage(source, sw - 1, sh - 1, 1, 1, dx + dw - 1, dy + dh - 1, R + 1, B + 1);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, dx, dy, dw, dh);

    var jpeg = null;
    var qualities = [0.92, 0.85, 0.75, 0.65];
    for (var i = 0; i < qualities.length; i++) {
      jpeg = await canvasToBlob(canvas, 'image/jpeg', qualities[i]);
      if (jpeg.size <= MAX_UPLOAD_BYTES) break;
    }
    if (jpeg.size > MAX_UPLOAD_BYTES) throw new Error('Print file too large even at reduced quality');

    var n = opts.bannerNumber || 1;
    var sizeTag = fmt(widthIn) + 'x' + fmt(heightIn) + 'in';
    var stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    var baseName = 'BB-print-' + stamp + '-banner' + n + '-' + sizeTag;

    var pdf = buildPdf(new Uint8Array(await jpeg.arrayBuffer()), W, H, widthIn, heightIn, baseName);

    // Proof preview (small JPEG).
    var ps = Math.min(1, PROOF_MAX_SIDE / Math.max(W, H));
    var proof = document.createElement('canvas');
    proof.width = Math.max(1, Math.round(W * ps));
    proof.height = Math.max(1, Math.round(H * ps));
    var pctx = proof.getContext('2d', { alpha: false });
    pctx.imageSmoothingQuality = 'high';
    pctx.drawImage(canvas, 0, 0, proof.width, proof.height);
    var proofBlob = await canvasToBlob(proof, 'image/jpeg', 0.85);

    // Free the big canvas before uploading.
    canvas.width = canvas.height = 1;

    var result = {
      artworkSource: kind === 'design' ? 'Designed in builder' : (kind === 'pdf' ? 'Uploaded PDF' : 'Uploaded image'),
      printDpi: dpi,
      printPixels: W + ' x ' + H
    };
    result.printFileUrl = await upload(opts.uploadApi, pdf, baseName + '.pdf');
    result.printPreviewUrl = await upload(opts.uploadApi, proofBlob, baseName.replace('print', 'proof') + '.jpg');

    if (kind === 'design') {
      // Editable layout so the design can be reopened/adjusted in production. Optional.
      try {
        var json = new Blob([JSON.stringify(set.design)], { type: 'application/json' });
        result.designFileUrl = await upload(opts.uploadApi, json, baseName.replace('print', 'design') + '.json');
      } catch (e) {
        console.warn('Design file upload skipped:', e);
      }
    }
    return result;
  }

  function fmt(n) { return String(Math.round(n * 100) / 100); }

  window.BBPrintFile = { prepare: prepare, buildPdf: buildPdf };
})();
